import "./bio_box.scss";

import { RenderHTML } from "@components/render_html";
import { useBreakpoint } from "@hooks/use_breakpoint";
import { htmlFromMarkdown } from "@root/react/helpers/markdown";
import classnames from "classnames";
import React, {
  FC,
  type MouseEventHandler,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

interface BioBoxProps {
  bioText: string;
}

type BioBoxPlainProps = Pick<BioBoxProps, "bioText">;
type BioBoxExpandoProps = Pick<BioBoxProps, "bioText">;

const BioBoxPlain: FC<BioBoxPlainProps> = ({ bioText }) => {
  const bioHtml = useMemo(() => {
    return htmlFromMarkdown(bioText);
  }, [bioText]);

  return (
    <div className="bio-box--container">
      <div className={classnames("bio-box--bio markdown")}>
        <RenderHTML html={bioHtml} />
      </div>
    </div>
  );
};

const BioBoxExpando: FC<BioBoxExpandoProps> = ({ bioText }) => {
  const bioHtml = useMemo(() => {
    return htmlFromMarkdown(bioText);
  }, [bioText]);

  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleExpanded: MouseEventHandler = useCallback(
    (e) => {
      const currentlyOpen = expanded;
      e.preventDefault();
      setExpanded(!expanded);
      if (currentlyOpen && containerRef) {
        // we're closing the accordion so scroll up
        setTimeout(() => {
          if (!containerRef.current) {
            return;
          }
          containerRef.current.scrollIntoView({
            inline: "nearest",
            block: "start",
            behavior: "smooth",
          });
        }, 10);
      }
    },
    [containerRef, expanded],
  );

  return (
    <div className="bio-box--container" ref={containerRef}>
      <div
        className={classnames("bio-box--bio markdown", {
          "bio-box--collapsed": !expanded,
        })}
      >
        <RenderHTML html={bioHtml} />
        {!expanded && <div className="bio-box--fade-out" />}
      </div>
      <div className="bio-box--controls">
        <a
          href="#"
          onClick={toggleExpanded}
          className="btn secondary bio-box--btn"
        >
          {expanded ? <>&laquo; read less</> : <>read more &raquo;</>}
        </a>
      </div>
    </div>
  );
};

const MAX_NUM_WORDS = 175;

const BioBox: FC<BioBoxProps> = ({ bioText }) => {
  const { isMobile } = useBreakpoint();
  const numWords = useMemo(() => bioText.split(/\s+/).length, [bioText]);

  const showWithExpando = !isMobile && numWords > MAX_NUM_WORDS;
  return (
    <div>
      {showWithExpando ? (
        <BioBoxExpando bioText={bioText} />
      ) : (
        <BioBoxPlain bioText={bioText} />
      )}
    </div>
  );
};

export { BioBox, BioBoxProps };
